import React, { useState } from 'react';
import { Box, Flex, IconButton } from '@chakra-ui/react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../core/hooks/storeHooks';
import { selectActiveWindow } from '../../../../windows/redux/windowsSlice';
import { ApplicationType } from '../../../../windows/types';
import {
  selectCellStyle,
  selectSelectedSheetAreas,
  selectSheetAndView,
} from '../../../../sheet/selectors/sheetSelector';
import { createArrayAreaAddressFromPosition } from '../../../../sheet/utils/area/area';
import {
  AiOutlineBold,
  AiOutlineItalic,
  AiOutlineStrikethrough,
  AiOutlineUnderline,
} from 'react-icons/ai';
import { BsClipboard2Data, BsPaintBucket } from 'react-icons/bs';
import { IconType } from 'react-icons';
import { Menu } from '@szhsin/react-menu';
import BasicColorPicker from '../../../../app/components/pickers/BasicColorPicker';
import { MdFormatColorText } from 'react-icons/md';
import NumberFormatModal from '../../../../app/components/modals/numberFormat/NumberFormatModal';
import { GoNumber } from 'react-icons/go';
import { handleSheetOperations } from '../../../../sheet/redux/sagas/root/rootActions';
import { SheetCommandType } from '../../../../sheet/types/commands/sheetCommands/sheetCommands';
import _ from 'lodash';
import CreateValidation from '../../../../app/components/modals/dataValidation/createValidation';
import { FontStyle, Style } from '../../../../sheet/types';

interface CompactToolbarProps {
  height: number;
}

interface IColorMenuItem {
  Icon: IconType;
  value: string;
  size?: number;
  onSelect: (color: string) => void;
}

// const getCellStyles = (
//   sheet: Sheet,
//   position: CellPosition
// ): Style => {
//   const address = convertPositionToAddress(position);
//   // if (sheet.cellStyles[address]) return sheet.cellStyles[address];

//   // const cellArea = convertPositionToArea(position);

//   // for (let range in sheet.styles) {
//   //   const rangeArea = convertParsedAddressToArea(
//   //     parseSingleAddress(range),
//   //     sheet.maxColIndex,
//   //     sheet.maxRowIndex
//   //   );
//   //   if (isAreaInsideArea(cellArea, rangeArea)) {
//   //     return sheet.styles[range];
//   //   }
//   // }
//   return {};
// };

const CompactToolBar: React.FC<CompactToolbarProps> = ({ height }) => {
  const dispatch = useAppDispatch();
  const activeWindow = useAppSelector((state) => selectActiveWindow(state));
  const activeSheetId =
    activeWindow && activeWindow.metaData.appType === ApplicationType.Sheet
      ? activeWindow.metaData.sheetId
      : '';

  const [state, setState] = useState({
    numberFormatModalOpen: false,
    dataValidationModalOpen: false,
  });

  const selectedAreas = useAppSelector((state) =>
    selectSelectedSheetAreas(state, activeSheetId)
  );

  const toggleModal = (key: string, value: boolean) => {
    setState({
      ...state,
      [key]: value,
    });
  };

  const styles: Style = useAppSelector((state) => {
    const { sheet, sheetView } = selectSheetAndView(state, activeSheetId);
    if (sheet && sheetView) {
      const { cellsReferences, stylesMatrix, styles } = sheet;
      const position = sheetView.activeCell;
      const arrayAddress = createArrayAreaAddressFromPosition(position);
      return selectCellStyle(stylesMatrix, styles, position, arrayAddress);
    }
    return {};
  });

  const handleUpdateStyle = (style: Style) => {
    dispatch(
      handleSheetOperations({
        sheetId: activeSheetId,
        type: SheetCommandType.Styles,
        styles: selectedAreas.map((area) => ({
          area,
          style: _.cloneDeep(style),
        })),
      })
    );
  };
  const toggleButton = (button: string, value: any) => {
    const style: Style = {
      font: {
        [button]: value,
      },
    };
    handleUpdateStyle(style);
    // dispatch(
    //   handleCellOperationsAc({
    //     sheetId: activeSheetId,
    //     operationData: {
    //       op: CellOperations.Format,
    //       style: {
    //         font: {
    //           [button]: value,
    //         },
    //       },
    //     },
    //   })
    // );
  };

  const onBgColorSelect = (color: string | null) => {
    const style: Style = {
      fill: {
        backgroundColor: color,
      },
    };
    handleUpdateStyle(style);

    // dispatch(
    //   handleCellOperationsAc({
    //     sheetId: activeSheetId,
    //     operationData: {
    //       op: CellOperations.Format,
    //       style: {
    //         fill: {
    //           backgroundColor: color,
    //         },
    //       },
    //     },
    //   })
    // );
  };

  const colorMenuView = (
    Icon: IconType,
    value: string,
    onSelect: (color: string | null) => void
  ) => (
    <Menu
      menuStyle={{ padding: 0 }}
      menuButton={
        <IconButton
          icon={<Icon />}
          variant="ghost"
          aria-label="bold"
          size="md"
          m="1"
          borderRadius={'50%'}
        />
      }
      portal={true}
      position="anchor"
      unmountOnClose={true}
    >
      <BasicColorPicker onSelect={onSelect} value={value} />
    </Menu>
  );

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      p={2}
      height={height + 'px'}
    >
      <Box mr={4}>
        {['bold', 'italic', 'underline', 'strikethrough'].map((key) => {
          const value = styles.font?.[key as keyof FontStyle];
          const Icon = getIcon(key);
          return (
            <IconButton
              key={key}
              icon={<Icon />}
              isActive={!!value}
              variant="ghost"
              onClick={() => toggleButton(key, value ? undefined : true)}
              aria-label={key}
              size="md"
              m="1"
              borderRadius={'50%'}
            />
          );
        })}
        {colorMenuView(
          BsPaintBucket,
          styles.fill?.backgroundColor ?? '',
          onBgColorSelect
        )}
        {colorMenuView(
          MdFormatColorText,
          styles.font?.color ?? '',
          (color: string | null) => toggleButton('color', color)
        )}
        <IconButton
          icon={<GoNumber />}
          variant="ghost"
          onClick={() => toggleModal('numberFormatModalOpen', true)}
          aria-label="number-format"
          size="md"
          m="1"
          borderRadius={'50%'}
        />

        <IconButton
          icon={<BsClipboard2Data />}
          variant="ghost"
          onClick={() => toggleModal('dataValidationModalOpen', true)}
          aria-label="data-validation"
          size="md"
          m="1"
          borderRadius={'50%'}
        />
      </Box>

      {state.numberFormatModalOpen && (
        <NumberFormatModal
          sheetId={activeSheetId}
          onClose={() => toggleModal('numberFormatModalOpen', false)}
        />
      )}

      {state.dataValidationModalOpen && (
        <CreateValidation
          sheetId={activeSheetId}
          onClose={() => toggleModal('dataValidationModalOpen', false)}
        />
      )}

      {/* Add Font Size and Font Family Dropdowns */}
      {/* Add your Font Size and Font Family Dropdowns here */}

      {/* Responsive Menu for Additional Buttons */}
      {/* <Menu>
        <MenuButton as={IconButton} icon={<FaEllipsisH />} variant="outline" />
        <MenuList>
          <MenuItem>
            <IconButton
              icon={<FaBold />}
              isActive={selectedButtons.includes('bold')}
              onClick={() => toggleButton('bold')}
              aria-label="bold"
            />
          </MenuItem>
        </MenuList>
      </Menu> */}
    </Flex>
  );
};

const getIcon = (key: string) => {
  let Icon = AiOutlineBold;
  switch (key) {
    case 'italic':
      return AiOutlineItalic;
    case 'underline':
      return AiOutlineUnderline;
    case 'strikethrough':
      return AiOutlineStrikethrough;
  }
  return Icon;
};

export default CompactToolBar;
